<template>
    <div>
        <div class="content bg-gray-lighter ng-scope">
            <div class="row items-push">
                <div class="col-sm-10">
                    <h1 class="page-heading">
                        Generazione File XML Bonifici (SEPA/CBI)
                        <small>Visualizzazione fatture periti, generazione file XML pagamenti e invio via email alla direzione/amministrazione.</small>
                    </h1>
                </div>
            </div>
        </div>

        <div class="content">
            <!-- Box Filtri -->
            <div class="block">
                <div class="block-header bg-gray-lighter">
                    <h3 class="block-title">Filtri di ricerca</h3>
                </div>
                <div class="block-content">
                    <div class="row">
                        <div class="col-sm-12 col-md-4">
                            <div class="form-group">
                                <label>Filtro per data fattura</label>
                                <date-range-picker
                                    style="width: 100%;"
                                    ref="picker"
                                    :locale-data="localeRange"
                                    :autoApply="true"
                                    :ranges="false"
                                    v-model="dateRange"
                                    @select="onDateRangeChange"
                                >
                                    <template v-slot:input="picker" style="width:100%;">
                                        <span v-if="picker.startDate">{{ picker.startDate | toEuropeData }} - {{ picker.endDate | toEuropeData }}</span>
                                        <span v-else>Tutte le date</span>
                                    </template>
                                </date-range-picker>
                            </div>
                        </div>

                        <div class="col-sm-12 col-md-4">
                            <div class="form-group">
                                <label>Cerca per perito o n° fattura</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="search.search"
                                    @keyup.enter="applicaFiltri"
                                    placeholder="Es. Rossi o 12/2026..."
                                />
                            </div>
                        </div>

                        <div class="col-sm-12 col-md-4">
                            <div class="form-group">
                                <label>Stato IBAN</label>
                                <select class="form-control" v-model="search.has_iban" @change="applicaFiltri">
                                    <option value="">Tutte le fatture</option>
                                    <option value="1">Solo con IBAN presente</option>
                                    <option value="0">Solo con IBAN mancante</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="row" style="margin-bottom: 15px;">
                        <div class="col-sm-12 text-right">
                            <button class="btn btn-sm btn-secondary mr-2" type="button" @click="resetFiltri">
                                <i class="fa fa-undo"></i> Reset Filtri
                            </button>
                            <button class="btn btn-sm btn-primary" type="button" @click="applicaFiltri">
                                <i class="fa fa-search"></i> Applica Filtri
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabella Fatture -->
            <div class="block">
                <div class="block-header bg-gray-lighter d-flex justify-content-between align-items-center">
                    <h3 class="block-title">Elenco Fatture</h3>
                    <div class="block-options">
                        <span class="badge badge-primary font-size-sm" style="font-size: 14px; padding: 6px 12px;">
                            Selezionate: {{ selezionateCount }} | Totale Bonifici: € {{ totaleSelezionate }}
                        </span>
                    </div>
                </div>

                <div class="block-content block-content-full">
                    <!-- Barra delle Azioni Principali -->
                    <div class="row mb-3" style="margin-bottom: 20px;">
                        <div class="col-sm-12 col-md-6 d-flex align-items-center">
                            <div class="custom-control custom-checkbox mr-3">
                                <input
                                    type="checkbox"
                                    class="custom-control-input"
                                    id="checkAll"
                                    v-model="selezionaTutto"
                                    @change="toggleSelezionaTutto"
                                />
                                <label class="custom-control-label font-weight-bold" for="checkAll">
                                    Seleziona tutti nella pagina
                                </label>
                            </div>
                            <span v-if="fattureConIbanMancanteSelezionate > 0" class="text-danger ml-3" style="font-size: 13px;">
                                <i class="fa fa-exclamation-triangle"></i> Attenzione: {{ fattureConIbanMancanteSelezionate }} fattura/e selezionata/e senza IBAN valido!
                            </span>
                        </div>

                        <div class="col-sm-12 col-md-6 text-right">
                            <button
                                class="btn btn-success mr-2"
                                type="button"
                                :disabled="selezionateCount === 0 || loadingAction"
                                @click="scaricaXml"
                            >
                                <i class="fa fa-download"></i> Scarica XML Bonifici
                            </button>

                            <!--<button
                                class="btn btn-info"
                                type="button"
                                :disabled="selezionateCount === 0 || loadingAction"
                                @click="inviaEmail"
                            >
                                <i class="fa fa-paper-plane"></i> Invia XML via Email
                            </button>-->
                        </div>
                    </div>

                    <div v-if="fatture.length === 0" class="text-center py-4">
                        <h4>Nessuna fattura trovata</h4>
                    </div>

                    <div v-if="fatture.length > 0" class="table-responsive">
                        <table class="table table-bordered table-striped table-vcenter">
                            <thead>
                                <tr>
                                    <th style="width: 50px;" class="text-center">#</th>
                                    <th>COD. PERITO</th>
                                    <th>PERITO</th>
                                    <th>N° FATTURA</th>
                                    <th>DATA FATTURA</th>
                                    <th>IBAN BENEFICIARIO</th>
                                    <th class="text-right">IMP. NETTO</th>
                                    <th class="text-right">TOT. FATTURA</th>
                                    <th class="text-center">STATO IBAN</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(f, index) in fatture" :key="index" :class="{'table-warning': f.selezionata && !hasValidIban(f)}">
                                    <td class="text-center">
                                        <input
                                            type="checkbox"
                                            v-model="f.selezionata"
                                            @change="aggiornaSelezione"
                                        />
                                    </td>
                                    <td>
                                        <strong>{{ f.perito ? f.perito.codice_perito : '-' }}</strong>
                                    </td>
                                    <td>
                                        <span v-if="f.perito">
                                            {{ f.perito.surname }} {{ f.perito.name }}
                                        </span>
                                        <span v-else class="text-muted">N/D</span>
                                        <div v-if="f.perito && f.perito.invoice && f.perito.invoice.soggetto_fiscale" class="text-muted font-size-sm">
                                            {{ f.perito.invoice.soggetto_fiscale }}
                                        </div>
                                    </td>
                                    <td>
                                        {{ f.n_fattura }}
                                    </td>
                                    <td>
                                        {{ f.data_fattura | toEuropeData }}
                                    </td>
                                    <td>
                                        <code v-if="hasValidIban(f)">{{ getIban(f) }}</code>
                                        <span v-else class="text-danger font-weight-bold">
                                            {{ getIban(f) ? getIban(f) + ' (non valido)' : 'Mancante' }}
                                        </span>
                                    </td>
                                    <td class="text-right font-w600 text-primary">
                                        € {{ formatPrice(f.imp_netto) }}
                                    </td>
                                    <td class="text-right">
                                        € {{ formatPrice(f.totale_fattura) }}
                                    </td>
                                    <td class="text-center">
                                        <span v-if="hasValidIban(f)" class="badge badge-success">OK</span>
                                        <span v-else class="badge badge-danger">IBAN MANCANTE</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Paginazione -->
                    <div v-if="pagination.last_page > 1" class="row mt-3">
                        <div class="col-sm-12 d-flex justify-content-center">
                            <paginate
                                v-model="pagination.current_page"
                                :page-count="pagination.last_page"
                                :click-handler="cambiaPagina"
                                :prev-text="'Precedente'"
                                :next-text="'Successivo'"
                                :container-class="'pagination'"
                                :page-class="'page-item'"
                                :prev-class="'prev'"
                                :next-class="'next'"
                            >
                            </paginate>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import Paginate from 'vuejs-paginate'
import BonificoXmlRepository from '../../services/apis/bonificoXml'
import Utility from '../../services/utility'

export default {
    name: 'BonificiXml',
    components: {
        DateRangePicker,
        Paginate
    },
    data() {
        return {
            dateRange: {},
            fatture: [],
            selezionaTutto: false,
            loadingAction: false,
            search: {
                search: '',
                has_iban: '',
                per_page: 25,
                page: 1,
                orderBy: 'data_fattura:desc'
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                total: 0
            }
        }
    },
    computed: {
        localeRange() {
            return Utility.dateRangeLocale();
        },
        selezionate() {
            return this.fatture.filter(f => f.selezionata);
        },
        selezionateCount() {
            return this.selezionate.length;
        },
        totaleSelezionate() {
            const sum = this.selezionate.reduce((acc, f) => {
                const val = parseFloat(f.totale_fattura) || 0;
                return acc + val;
            }, 0);
            return sum.toFixed(2);
        },
        fattureConIbanMancanteSelezionate() {
            return this.selezionate.filter(f => !this.hasValidIban(f)).length;
        }
    },
    mounted() {
        this.caricaFatture();
    },
    methods: {
        getIban(fattura) {
            if (fattura.perito && fattura.perito.invoice && fattura.perito.invoice.iban) {
                return fattura.perito.invoice.iban.trim();
            }
            return '';
        },
        hasValidIban(fattura) {
            const iban = this.getIban(fattura).replace(/\s/g, '');
            return iban.length >= 15;
        },
        formatPrice(val) {
            const num = parseFloat(val);
            if (isNaN(num)) return '0.00';
            return num.toFixed(2);
        },
        onDateRangeChange() {
            this.applicaFiltri();
        },
        applicaFiltri() {
            this.search.page = 1;
            this.pagination.current_page = 1;
            this.caricaFatture();
        },
        resetFiltri() {
            this.dateRange = {};
            this.search.search = '';
            this.search.has_iban = '';
            this.search.page = 1;
            this.pagination.current_page = 1;
            this.caricaFatture();
        },
        cambiaPagina(pageNum) {
            this.search.page = pageNum;
            this.caricaFatture();
        },
        toggleSelezionaTutto() {
            this.fatture.forEach(f => {
                f.selezionata = this.selezionaTutto;
            });
        },
        aggiornaSelezione() {
            this.selezionaTutto = this.fatture.length > 0 && this.fatture.every(f => f.selezionata);
        },
        async caricaFatture() {
            const params = { ...this.search };

            if (this.dateRange.startDate && this.dateRange.endDate) {
                params.start_date = Utility.convertDataToDB(this.dateRange.startDate);
                params.end_date = Utility.convertDataToDB(this.dateRange.endDate);
            }

            this.$store.dispatch('updateLoadingState', true);
            try {
                const resp = await BonificoXmlRepository.get_fatture(params);
                if (resp && resp.data && resp.data.data) {
                    const paginated = resp.data.data;
                    this.fatture = (paginated.data || []).map(item => ({
                        ...item,
                        selezionata: false
                    }));
                    this.pagination.current_page = paginated.current_page;
                    this.pagination.last_page = paginated.last_page;
                    this.pagination.total = paginated.total;
                    this.selezionaTutto = false;
                }
            } catch (err) {
                this.$toasted.error('Errore durante il caricamento delle fatture', { duration: 3000 });
            } finally {
                this.$store.dispatch('updateLoadingState', false);
            }
        },
        async scaricaXml() {
            if (this.selezionateCount === 0) {
                this.$toasted.error('Seleziona almeno una fattura da includere nel file XML.', { duration: 3000 });
                return;
            }

            if (this.fattureConIbanMancanteSelezionate > 0) {
                const proceed = confirm(
                    `Attenzione: ${this.fattureConIbanMancanteSelezionate} delle fatture selezionate non hanno un IBAN valido. Il bonifico fallirà per questi periti. Vuoi procedere comunque?`
                );
                if (!proceed) return;
            }

            const ids = this.selezionate.map(f => f.id);
            this.loadingAction = true;
            this.$store.dispatch('updateLoadingState', true);

            try {
                const resp = await BonificoXmlRepository.download_xml({ fatture_ids: ids });
                
                let filename = 'bonifici.xml';
                const disposition = resp.headers['content-disposition'];
                if (disposition && disposition.indexOf('filename=') !== -1) {
                    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                    if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, '');
                    }
                }

                const blob = new Blob([resp.data], { type: 'application/xml;charset=utf-8' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.$toasted.success('File XML generato e scaricato con successo!', { duration: 3000 });
            } catch (err) {
                let msg = 'Errore durante la generazione del file XML.';
                if (err.response && err.response.data) {
                    if (err.response.data instanceof Blob) {
                        try {
                            const errText = await err.response.data.text();
                            const parsed = JSON.parse(errText);
                            if (parsed.message) msg = parsed.message;
                        } catch (e) {
                            // Ignora fallback su blob text
                        }
                    } else if (err.response.data.message) {
                        msg = err.response.data.message;
                    }
                }
                this.$toasted.error(msg, { duration: 6000 });
            } finally {
                this.loadingAction = false;
                this.$store.dispatch('updateLoadingState', false);
            }
        },
        async inviaEmail() {
            if (this.selezionateCount === 0) {
                this.$toasted.error('Seleziona almeno una fattura da inviare.', { duration: 3000 });
                return;
            }

            if (this.fattureConIbanMancanteSelezionate > 0) {
                this.$toasted.error('Impossibile inviare l\'XML: alcune fatture selezionate non hanno un IBAN valido compilato.', { duration: 5000 });
                return;
            }

            const confirmSend = confirm(
                `Confermi l'invio del file XML via email per ${this.selezionateCount} fatture (totale € ${this.totaleSelezionate}) al destinatario predefinito?`
            );
            if (!confirmSend) return;

            const ids = this.selezionate.map(f => f.id);
            this.loadingAction = true;
            this.$store.dispatch('updateLoadingState', true);

            try {
                const resp = await BonificoXmlRepository.send_email({ fatture_ids: ids });
                if (resp && resp.data && resp.data.status === 'success') {
                    this.$toasted.success(resp.data.message || 'File XML inviato via email con successo!', { duration: 4000 });
                } else {
                    this.$toasted.error((resp.data && resp.data.message) || 'Errore durante l\'invio dell\'email', { duration: 4000 });
                }
            } catch (err) {
                const msg = (err.response && err.response.data && err.response.data.message) || 'Errore durante l\'invio via email.';
                this.$toasted.error(msg, { duration: 6000 });
            } finally {
                this.loadingAction = false;
                this.$store.dispatch('updateLoadingState', false);
            }
        }
    }
}
</script>

<style scoped>
.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #5c80d1;
    border-color: #5c80d1;
}
.mr-2 {
    margin-right: 0.5rem;
}
.mr-3 {
    margin-right: 1rem;
}
.ml-3 {
    margin-left: 1rem;
}
.mb-3 {
    margin-bottom: 1rem;
}
.mt-3 {
    margin-top: 1rem;
}
.py-4 {
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
}
</style>
